import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Transaction from 'models/Transaction';
import { refreshDataAddToCacheOnly, submitBudgetUpdate } from 'redux/actions/dashboard';
import budgetBlockRows from 'utilities/ledger/budgetBlockRows';
import recordingRows from 'utilities/ledger/recordingRows';
import { budgetPane, PANE_BUDGET, PANE_RECORDINGS, recordingsPane } from 'utilities/ledger/ledgerPanes';
import LedgerGrid from 'react/components/ledger/LedgerGrid';
import LedgerBudgetBlock from 'react/components/ledger/LedgerBudgetBlock';
import LedgerRecordingsBlock from 'react/components/ledger/LedgerRecordingsBlock';

/**
 * The Ledger -- the spreadsheet she actually wants back.
 *
 * Her sheet is one month per screen with the budget down the left and the expense recordings
 * beside it, and the point of it is that everything is visible at once: she scans the budget block
 * for what is left in a category while she types the row that spends it. So the two are on one
 * screen here rather than on two tabs.
 *
 * The month comes from the month control in `<AppHeader>`, which sits above the section and so
 * already applies to this one. Selecting more than one month is additive by construction: every amount in the budget block
 * comes from `getMultiBudget()`, which sums across the selection. Budgetted stops being editable
 * when it does, because a sum across six months has no single budget row to write back to --
 * `submitBudgetUpdate` posts against one year and month, and it would silently pick one of them.
 *
 * Both tables are rebuilt from the models on every commit rather than being kept in local state.
 * The models here are mutable and shared -- a Category holds its budgets, the finder holds the
 * transactions, and other screens hold the same objects -- so a local copy is a second answer to
 * the same question that starts drifting the moment anything else writes to them. `revision` is
 * what makes a mutation in place visible: nothing about the objects themselves changes identity.
 */
export default function LedgerPage({ categoryGroupsByType, selectedDates, selectedDate, transactionFinder, transactionSubmit }) {
	const dispatch = useDispatch();
	const lastChangedBudget = useSelector((state) => state.dashboard.lastChangedBudget);
	const refreshingData = useSelector((state) => state.dashboard.refreshingData);
	const transactionFinderUpdates = useSelector((state) => state.dashboard.transactionFinderUpdates);
	const [revision, setRevision] = useState(0);
	const [draft, setDraft] = useState(() => blankTransaction(selectedDate));

	const redraw = useCallback(() => setRevision((count) => count + 1), []);

	// Following the month selector: an untouched blank row belongs to whatever month is on screen,
	// but a half-typed one is hers and is left where it is rather than being silently re-dated.
	useEffect(() => {
		setDraft((current) => (current.where || current.amount ? current : blankTransaction(selectedDate)));
	}, [selectedDate]);

	const singleMonth = Object.keys(selectedDates || {}).length === 1;

	const panes = useMemo(() => ([
		budgetPane(budgetBlockRows(categoryGroupsByType, selectedDates), singleMonth),
		recordingsPane({ ...recordingRows(transactionFinder, selectedDates), draft }),
	]), [
		categoryGroupsByType,
		selectedDates,
		singleMonth,
		transactionFinder,
		transactionFinderUpdates,
		lastChangedBudget,
		refreshingData,
		draft,
		revision,
	]);

	const budgetCommit = useCallback((category, cents) => {
		if(!singleMonth) {
			return;
		}
		const budget = category.getBudget(selectedDate);
		budget.update({ amount: cents });
		redraw();
		dispatch(submitBudgetUpdate(category, budget))
			.then(() => dispatch(refreshDataAddToCacheOnly()));
	}, [singleMonth, selectedDate, dispatch, redraw]);

	const recordingCommit = useCallback((row, field, value) => {
		const { transaction } = row;
		// Assigned rather than `update()`d: the field values here are already in the model's own
		// terms -- cents, a moment, a category id -- and `dataLocationParsers` would re-parse them
		// out of the server's.
		transaction[field] = value;
		redraw();

		if(row.kind !== 'new') {
			transactionSubmit(transaction);
			return;
		}
		// A blank row is not a transaction until it says something. Amount alone counts: an
		// unnamed $40 is still a row she will want to see and name later, and a name alone is a
		// row half typed.
		if(!transaction.where && !transaction.amount) {
			return;
		}
		transactionSubmit(transaction);
		setDraft(blankTransaction(selectedDate));
	}, [transactionSubmit, selectedDate, redraw]);

	return (
		<LedgerGrid panes={panes} className='ledger_page'>
			<section className='ledger_budgets'>
				<LedgerBudgetBlock
					pane={panes[PANE_BUDGET]}
					paneIndex={PANE_BUDGET}
					budgetCommit={budgetCommit}
				/>
			</section>
			<section className='ledger_recordings'>
				<h6 className='region_heading'>
					Expense Recordings
					<span className='hint'>Arrows to move, Enter or type to edit, Escape to back out, shift or ctrl to select several</span>
				</h6>
				<LedgerRecordingsBlock
					pane={panes[PANE_RECORDINGS]}
					paneIndex={PANE_RECORDINGS}
					selectedDates={selectedDates}
					commit={recordingCommit}
				/>
			</section>
		</LedgerGrid>
	);
}

/**
 * The empty row at the bottom.
 *
 * Dated to today when today is in the month on screen and to the first of it otherwise, because a
 * date is the one field she should never have to fill in: entering January in February means every
 * row is a January row, and entering today's spending means every row is today.
 */
function blankTransaction(selectedDate) {
	const today = moment();
	const date = (selectedDate && today.isSame(selectedDate, 'month'))
		? today
		: (selectedDate ? selectedDate.clone().startOf('month') : today);
	return Transaction.create({
		where: '',
		amount: 0,
		transaction_date: date.unix(),
	});
}
